import { useEffect, useRef, useState, type FormEvent } from 'react';
import { BotaoGoogle } from '../../components/BotaoGoogle';
import { BotaoDiscord, lerErroDoDiscordNaEntrada } from '../../components/BotaoDiscord';
import { ConfirmarEmail } from '../../components/ConfirmarEmail';
import { CodigoDoisFatores } from '../../components/CodigoDoisFatores';
import { RedefinirSenha } from '../../components/RedefinirSenha';
import { SimboloMarca } from '../../components/marca/SimboloMarca';
import { t } from '../../i18n/idioma';
import { useConvite } from '../../origem/visita';
import { useSession } from '../../state/SessionContext';
import { EMAIL_MAX, SENHA_MAX, SENHA_MIN, validarEmail, validarSenha } from '../entrada/validar';
import { Seta } from './icones';

export type AbaLogin = 'entrar' | 'criar' | 'convite';

/**
 * O código do convite como veio (com ou sem traços, minúsculo, colado com espaço): só letras e números, e os traços
 * no lugar (o prefixo da fase e três grupos de 4), como a tela de entrada sempre mandou.
 */
export function normalizarCodigo(bruto: string): string {
  const limpo = bruto.replace(/[^0-9a-zA-Z]/g, '').toUpperCase();
  if (limpo.length <= 12) return limpo;
  const corpo = limpo.slice(-12);
  return [limpo.slice(0, -12), corpo.slice(0, 4), corpo.slice(4, 8), corpo.slice(8, 12)].join('-');
}

/** O código tem o tamanho de um convite (16 letras e números ou mais, sem os traços). */
const codigoCompleto = (codigo: string) => codigo.replace(/-/g, '').length >= 16;

/**
 * O cartão de login da landing (docs/prototipos/landing-aberta-nota.md §03): as abas da tela de entrada, com a cara
 * do cartão da landing. Mesmas rotas, regras e mensagens da antiga tela de login (removida em 25/10/2026); quem entra sai desta página para a escolha
 * (ou a criação) do personagem, porque a sessão muda.
 *
 * - `modo="aberto"` (landing aberta): Entrar e Criar conta.
 * - `modo="convite"` (landing da fase, techspec-landing-fase §06.7): Entrar e Convite, a conta nasce com o código do
 *   e-mail do sorteio; em Entrar, "Tenho um código" manda o código junto (a conta que já existe ganha o acesso).
 *
 * Os ids `email-entrar` e `senha-entrar` são os da tela de entrada: os testes de navegador entram pelos dois.
 */
export function LoginCard({ aba, onAba, modo = 'aberto', codigoInicial = '', onPreRegistro, emailInicial = '', confirmar, senhaLink }: {
  aba: AbaLogin;
  onAba(aba: AbaLogin): void;
  /** `convite`: o jogo está numa fase de testes; conta nova só com o código. */
  modo?: 'aberto' | 'convite';
  /** O código que veio no link do convite (`/convite#c=`). */
  codigoInicial?: string;
  /** Na landing da fase: volta o cartão para o pré-registro (quem ainda não tem convite). */
  onPreRegistro?(): void;
  /** Veio pelo link do acesso manual: o e-mail já vem no campo (o `*` é o exemplo do console, não preenche). */
  emailInicial?: string;
  /** Veio pelo botão do e-mail de confirmação: abre direto o passo do código, já preenchido. */
  confirmar?: { readonly email: string; readonly codigo: string };
  /** Veio pelo botão do e-mail de "esqueci a senha": abre a senha nova com o e-mail e o código preenchidos. */
  senhaLink?: { readonly email: string; readonly codigo: string };
}) {
  const { signIn, signUp, signInGoogle, busy, verificando, error, errorCode, clearError, confirmacao, iniciarConfirmacao, doisFatores } = useSession();
  const convite = useConvite();
  const [email, setEmail] = useState(emailInicial.includes('*') ? '' : emailInicial);
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const [campo, setCampo] = useState<'email' | 'senha' | 'codigo' | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [recuperar, setRecuperar] = useState(!!senhaLink);
  const [codigo, setCodigo] = useState(() => normalizarCodigo(codigoInicial));
  const [comCodigo, setComCodigo] = useState(false);
  const fase = modo === 'convite';
  const outra: AbaLogin = fase ? 'convite' : 'criar';
  const conviteAba = aba === 'convite';
  // Conta nova: Criar conta (aberto) ou Convite (fase). As duas pedem as regras da senha.
  const criar = aba === 'criar' || conviteAba;
  /** O código digitado, para ir junto no login, no Google, na confirmação e na senha nova. */
  const codigoParaEnviar = codigo && (conviteAba || comCodigo) ? normalizarCodigo(codigo) : undefined;
  const travado = busy || verificando;
  const erro = erroLocal ?? error;

  useEffect(() => {
    // Voltou do Discord com erro (techspec-discord-cargos §03): mostra o motivo e limpa a URL.
    const erroDiscord = lerErroDoDiscordNaEntrada();
    if (erroDiscord) setErroLocal(erroDiscord);
    if (confirmar?.email) iniciarConfirmacao(confirmar.email);
    else if (email) document.getElementById(`senha-${aba}`)?.focus({ preventScroll: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const limpar = () => { setErroLocal(null); setCampo(null); setAviso(null); clearError(); };
  const campoRuim = (qual: 'email' | 'senha' | 'codigo') => campo === qual;
  const trocar = (nova: AbaLogin) => { if (nova !== aba) onAba(nova); };

  // A aba também muda por fora (os botões da página): a troca limpa os avisos e a senha. O aviso que já veio na
  // abertura (sessão vencida, por exemplo) fica.
  const abaAnterior = useRef(aba);
  useEffect(() => {
    if (abaAnterior.current === aba) return;
    abaAnterior.current = aba;
    limpar();
    setSenha('');
  }, [aba]); // eslint-disable-line react-hooks/exhaustive-deps

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (travado) return;
    limpar();
    const erroEmail = validarEmail(email);
    if (erroEmail) { setErroLocal(erroEmail); setCampo('email'); document.getElementById(`email-${aba}`)?.focus(); return; }
    const erroSenha = validarSenha(senha, email, criar);
    if (erroSenha) { setErroLocal(erroSenha); setCampo('senha'); document.getElementById(`senha-${aba}`)?.focus(); return; }
    const credenciais = { email: email.trim(), password: senha };
    if (conviteAba || (comCodigo && codigo)) {
      const serial = normalizarCodigo(codigo);
      if (!codigoCompleto(serial)) {
        setErroLocal(t('acesso.serialCurto'));
        setCampo('codigo');
        document.getElementById(`codigo-${aba}`)?.focus();
        return;
      }
      void (conviteAba ? signUp({ ...credenciais, serial }) : signIn({ ...credenciais, serial }));
      return;
    }
    void (criar ? signUp(credenciais) : signIn(credenciais));
  };

  // Entrar ou criar com o Google (techspec-login-google): o ID token vai para o servidor, que acha, liga ou cria a conta.
  const comGoogle = (credencial: string) => {
    if (travado) return;
    limpar();
    void signInGoogle(codigoParaEnviar ? { credencial, serial: codigoParaEnviar } : { credencial });
  };

  const regras = [
    { ok: senha.length >= SENHA_MIN, texto: t('entrada.regraComprimento') },
    { ok: senha.length > 0 && senha.toLowerCase() !== email.trim().toLowerCase(), texto: t('entrada.regraEmail') }
  ];

  // Conta da equipe: a senha conferiu e falta o código do app (techspec-console-2fa-e-cloudflare-access §03.4).
  if (doisFatores) {
    return (
      <>
        <div className="lp-abas">
          <button type="button" aria-selected>{t('doisFatores.titulo')}</button>
        </div>
        <CodigoDoisFatores variante="landing" />
      </>
    );
  }

  // A conta nasceu (ou a senha conferiu) e falta o código do e-mail (techspec-confirmacao-email).
  if (confirmacao) {
    return (
      <>
        <div className="lp-abas">
          <button type="button" aria-selected>{t('confirmar.titulo')}</button>
        </div>
        <ConfirmarEmail variante="landing" codigoInicial={confirmar?.email === confirmacao ? confirmar.codigo : ''}
          serial={codigoParaEnviar} />
      </>
    );
  }

  // "Esqueci a senha" (techspec-esqueci-senha): o cartão vira o pedido do código e a senha nova.
  if (recuperar) {
    return (
      <>
        <div className="lp-abas">
          <button type="button" aria-selected>{t('senha.titulo')}</button>
        </div>
        <RedefinirSenha variante="landing" emailInicial={senhaLink?.email ?? email} codigoInicial={senhaLink?.codigo ?? ''}
          serial={codigoParaEnviar} onVoltar={() => setRecuperar(false)} />
      </>
    );
  }

  return (
    <>
      {convite && (
        // O link de convite de um jogador (techspec-indicacoes §03.1): quem convidou, em cima das abas.
        <div className="lp-convite" role="note">
          <SimboloMarca className="lp-convite__ico" />
          <div>
            <b>{t('landing.convite.titulo', { nome: convite })}</b>
            <span>{t('landing.convite.texto')}</span>
          </div>
        </div>
      )}
      <div className="lp-abas" role="tablist" aria-label={t(fase ? 'landing.fase.abas' : 'landing.aberta.abas')}>
        <button type="button" role="tab" id="lp-aba-entrar" aria-selected={!criar} aria-controls="lp-login"
          onClick={() => trocar('entrar')}>{t('entrada.abaEntrar')}</button>
        <button type="button" role="tab" id={`lp-aba-${outra}`} aria-selected={criar} aria-controls="lp-login"
          onClick={() => trocar(outra)}>{t(fase ? 'acesso.abaConvite' : 'entrada.abaCriar')}</button>
      </div>
      <form className="lp-gump__corpo" id="lp-login" role="tabpanel" aria-labelledby={criar ? `lp-aba-${outra}` : 'lp-aba-entrar'}
        onSubmit={enviar} noValidate>
        {conviteAba && <p className="lp-miudo lp-miudo--topo">{t('landing.fase.conviteTexto')}</p>}
        <div className={campoRuim('email') ? 'lp-campo is-ruim' : 'lp-campo'}>
          <label htmlFor={`email-${aba}`}>{t('auth.email')}</label>
          <input
            id={`email-${aba}`}
            name="email"
            type="email"
            value={email}
            placeholder={t('landing.form.emailExemplo')}
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={EMAIL_MAX}
            disabled={travado}
            aria-invalid={campo === 'email' || undefined}
            aria-describedby={erro ? 'lp-login-erro' : undefined}
            onChange={e => { setEmail(e.target.value); limpar(); }}
          />
        </div>
        {(conviteAba || comCodigo) && (
          <div className={campoRuim('codigo') ? 'lp-campo is-ruim' : 'lp-campo'}>
            <label htmlFor={`codigo-${aba}`}>{t('acesso.serial')}</label>
            <input
              id={`codigo-${aba}`}
              name="codigo"
              type="text"
              className="lp-codigo"
              value={codigo}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={40}
              disabled={travado}
              aria-invalid={campoRuim('codigo') || undefined}
              onChange={e => { setCodigo(e.target.value.toUpperCase()); limpar(); }}
              onBlur={() => setCodigo(c => normalizarCodigo(c))}
            />
          </div>
        )}
        <div className={campoRuim('senha') ? 'lp-campo is-ruim' : 'lp-campo'}>
          <div className="lp-campo__linha">
            <label htmlFor={`senha-${aba}`}>{t('comum.senha')}</label>
            {!criar && (
              <button type="button" className="lp-elo" onClick={() => { limpar(); setRecuperar(true); }}>
                {t('entrada.esqueciSenha')}
              </button>
            )}
          </div>
          <div className="lp-senha">
            <input
              id={`senha-${aba}`}
              name="password"
              type={mostrar ? 'text' : 'password'}
              value={senha}
              placeholder={criar ? t('entrada.dicaSenha') : '••••••••'}
              autoComplete={criar ? 'new-password' : 'current-password'}
              maxLength={SENHA_MAX}
              disabled={travado}
              aria-invalid={campo === 'senha' || undefined}
              aria-describedby={criar ? 'lp-regras' : undefined}
              onChange={e => { setSenha(e.target.value); limpar(); }}
            />
            <button type="button" aria-label={mostrar ? t('entrada.ocultarSenha') : t('entrada.mostrarSenha')} aria-pressed={mostrar}
              onClick={() => setMostrar(v => !v)}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                <circle cx="12" cy="12" r="3" />
                {mostrar ? null : <path d="M4 20 20 4" />}
              </svg>
            </button>
          </div>
        </div>
        {criar && (
          <ul className="lp-regras" id="lp-regras">
            {regras.map(r => <li key={r.texto} className={r.ok ? 'is-ok' : undefined}>{r.ok ? '✓ ' : ''}{r.texto}</li>)}
          </ul>
        )}
        {verificando && <p className="lp-miudo">{t('entrada.verificando')}</p>}
        {erro && <p className="lp-aviso" id="lp-login-erro" role="alert">{erro}</p>}
        {fase && aba === 'entrar' && errorCode === 'acesso.semAcesso' && (
          // A conta existe mas não tem acesso a esta fase: o código do convite ou o pré-registro.
          <p className="lp-miudo">
            <button type="button" className="lp-elo" onClick={() => trocar('convite')}>{t('acesso.abaConvite')}</button>
            {onPreRegistro && <>{' · '}<button type="button" className="lp-elo" onClick={onPreRegistro}>{t('acesso.queroConcorrer')}</button></>}
          </p>
        )}
        {fase && aba === 'entrar' && !comCodigo && errorCode !== 'acesso.semAcesso' && (
          <button type="button" className="lp-elo lp-elo--centro" onClick={() => { setComCodigo(true); limpar(); }}>
            {t('acesso.tenhoSerial')}
          </button>
        )}
        {aviso && <p className="lp-aviso lp-aviso--nota" role="status">{aviso}</p>}
        <button type="submit" className="lp-btn lp-btn--ouro" disabled={travado}>
          {busy
            ? <><span className="lp-carregando" aria-hidden="true" /> {criar ? t('comum.criando') : t('auth.entrando')}</>
            : <>{conviteAba ? t('acesso.criarEEntrar') : criar ? t('landing.aberta.criarEJogar') : t('auth.entrarNoReino')} <Seta /></>}
        </button>
        <div className="lp-ou">{t('entrada.ou')}</div>
        {conviteAba && <p className="lp-miudo">{t('entrada.googleConvite')}</p>}
        <BotaoGoogle texto={criar ? 'signup_with' : 'continue_with'} desligado={travado} onCredencial={comGoogle} />
        {/* Entrar ou criar com o Discord: só aparece com o Discord ligado no servidor (o convite digitado vai junto). */}
        <BotaoDiscord texto={criar ? 'criar' : 'entrar'} desligado={travado} serial={codigoParaEnviar}
          onErro={m => { limpar(); setErroLocal(m); }} />
        <p className="lp-miudo">{criar ? t('landing.aberta.aceiteCriar') : t('entrada.aceite')}</p>
      </form>
      {fase ? (
        <div className="lp-gump__pe">
          {criar ? (
            <>
              {t('landing.aberta.jaTem')}{' '}
              <button type="button" className="lp-elo" onClick={() => trocar('entrar')}>{t('entrada.abaEntrar')}</button>
            </>
          ) : (
            <>
              {t('landing.form.convite')}{' '}
              <button type="button" className="lp-elo" onClick={() => trocar('convite')}>{t('landing.form.conviteLink')}</button>
            </>
          )}
          {onPreRegistro && (
            <>
              <br />
              {t('landing.fase.semConvite')}{' '}
              <button type="button" className="lp-elo" onClick={onPreRegistro}>{t('landing.fase.concorrer')}</button>
            </>
          )}
        </div>
      ) : (
        <div className="lp-gump__pe">
          {criar ? t('landing.aberta.jaTem') : t('landing.aberta.primeiraVez')}{' '}
          <button type="button" className="lp-elo" onClick={() => trocar(criar ? 'entrar' : 'criar')}>
            {criar ? t('entrada.abaEntrar') : t('landing.aberta.criarGratis')}
          </button>
        </div>
      )}
    </>
  );
}
